import React, { useCallback, useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { ChangelogEntry as ChangelogEntryType } from '@retail/changelog/types';
import { ChangelogEntry } from './ChangelogEntry';
import { useChangelogTFunction } from '@retail/changelog/i18n';
import { getDataGridLocalization, MgPrisLocale } from '@retail/utils';
import { useTranslation } from 'react-i18next';

export interface ChangelogEntriesMenuListProps {
  entries: ChangelogEntryType[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadMore: () => void;
  height?: number | string;
  width?: number | string;
}

interface ChangelogEntryRow extends ChangelogEntryType {
  isLoader: boolean;
}

export const ChangelogEntriesMenuList: React.FC<ChangelogEntriesMenuListProps> = ({
  entries,
  hasNextPage,
  isFetchingNextPage,
  loadMore,
  width = 760,
}) => {
  const t = useChangelogTFunction();
  const {
    i18n: { language },
  } = useTranslation();

  const rows: ChangelogEntryRow[] = useMemo(() => {
    const dataRows = entries.map((item) => ({
      ...item,
      isLoader: false,
    }));

    if (hasNextPage) {
      dataRows.push({
        id: 'loader',
        isLoader: true,
      } as any);
    }

    return dataRows;
  }, [entries, hasNextPage]);

  const columns: GridColDef<ChangelogEntryRow>[] = useMemo(
    () => [
      {
        field: 'entry',
        renderHeader: () => (
          <Typography variant="h4" component="h2">
            {t('changelog.lastChanges.title')}
          </Typography>
        ),
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          if (row.isLoader) {
            return (
              <Box sx={{ px: 2, width: '100%', display: 'flex', alignItems: 'center' }}>
                <Stack spacing={1} width="100%">
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="50%" />
                </Stack>
              </Box>
            );
          }

          return (
            <ChangelogEntry
              id={row.id}
              title={row.title}
              content={row.content}
              changeDate={row.changeDate}
              tags={row.tags}
              seen={row.seen}
            />
          );
        },
      },
    ],
    [t]
  );

  const handleRowsScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      loadMore();
    }
  }, [hasNextPage, isFetchingNextPage, loadMore]);

  return (
    <Box sx={{ width }}>
      <DataGridPremium
        rows={rows}
        columns={columns}
        hideFooter
        columnHeaderHeight={80}
        disableRowSelectionOnClick
        disableColumnResize
        disableColumnSelector
        disableMultipleColumnsFiltering
        disableMultipleColumnsSorting
        getRowHeight={() => 'auto'}
        localeText={{
          ...getDataGridLocalization(language as MgPrisLocale),
          noRowsLabel: t('changelog.lastChanges.none'),
        }}
        onRowsScrollEnd={handleRowsScrollEnd}
        sx={{
          border: 'none',
          '& .MuiDataGrid-cell': {
            maxHeight: 'none !important',
            overflow: 'visible',
            whiteSpace: 'normal',
            lineHeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            padding: 0,
            paddingRight: 4,
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
        }}
      />
    </Box>
  );
};
